// src/services/veturisApi.js
// Broadway Holidays — Veturis XML API
// Credentials sent as POST form params (NOT inside XML)

const PROXY = 'https://broadwayholidays.co.uk/veturis-proxy.php';

// ── Core proxy call ───────────────────────────────────────────
async function callProxy(xmlRQ) {
  const res = await fetch(PROXY, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ xmlRQ }),
  });
  const text = await res.text();
  const parser = new DOMParser();
  return parser.parseFromString(text, 'text/xml');
}

// ── Geography ─────────────────────────────────────────────────
export const DEST_HOTELS = {
  'marbella':  ['12077', '12085'],
  'spain':     ['12077', '12085'],
  'bruges':    ['9808', '168195'],
  'belgium':   ['9808', '168195'],
  'quito':     ['64749', '64276'],
  'ecuador':   ['64749', '64276'],
  'vietnam':   ['171219', '146503'],
  'hanoi':     ['146503'],
  'tenerife':  ['12077'],
  'lanzarote': ['12077'],
  'antalya':   ['12077'],
  'majorca':   ['12077'],
  'ibiza':     ['12077'],
  'rhodes':    ['12077'],
  'corfu':     ['12077'],
  'dubai':     ['12077'],
  'maldives':  ['12077'],
};

export function getHotelIdsForCity(city) {
  if (!city) return Object.values(DEST_HOTELS).flat().slice(0, 6);
  const key = city.toLowerCase().trim();
  if (DEST_HOTELS[key]) return DEST_HOTELS[key];
  for (const [k, ids] of Object.entries(DEST_HOTELS)) {
    if (key.includes(k) || k.includes(key)) return ids;
  }
  return ['12077', '9808', '12085', '64749', '168195', '146503'];
}

// ── 1. SearchAvailability ─────────────────────────────────────
// Supports two Veturis occupancy modes (docs v4.0 p8):
//   Option 1 — all rooms same occupancy: pass rooms/adults/children/childAges
//   Option 2 — rooms have different occupancy: pass roomOccupancies array
//              e.g. [{rooms:1,adults:2,children:0},{rooms:1,adults:2,children:1,childAges:[8]}]
export async function searchAvailability({
  hotelIds,
  checkIn,
  checkOut,
  rooms = 1,
  adults = 2,
  children = 0,
  childAges = [],
  roomOccupancies = null,   // Option 2: array of {rooms,adults,children,childAges}
  language = 'ENG',
}) {
  const hotelList = Array.isArray(hotelIds) ? hotelIds.join('/') : hotelIds;

  // Build occupancy XML ─────────────────────────────────────────
  // Option 2: multiple Occupancy tags (different occupancy per room group)
  let occupancyXml = '';
  if (roomOccupancies && roomOccupancies.length > 0) {
    occupancyXml = roomOccupancies.map(occ => {
      const ages = occ.childAges || [];
      const agesXml = ages.length > 0
        ? `<Ages>${ages.map(a => `<Age>${a}</Age>`).join('')}</Ages>`
        : '';
      return `<Occupancy><Rooms>${occ.rooms}</Rooms><Adults>${occ.adults}</Adults><Children>${occ.children || 0}</Children>${agesXml}</Occupancy>`;
    }).join('');
  } else {
    // Option 1: single Occupancy tag, same for all rooms
    // <Adults> and <Children> are PER ROOM; <Ages> lists all child ages (perRoom × rooms)
    const adultsPerRoom = rooms > 1 ? Math.floor(adults / rooms) : adults;
    const agesXml = childAges.length > 0
      ? `<Ages>${childAges.map(a => `<Age>${a}</Age>`).join('')}</Ages>`
      : '';
    occupancyXml = `<Occupancy><Rooms>${rooms}</Rooms><Adults>${adultsPerRoom}</Adults><Children>${children}</Children>${agesXml}</Occupancy>`;
  }

  const xmlRQ = `<?xml version="1.0" encoding="UTF-8"?>
<SearchAvailabilityRQ version="2.0" language="${language}">
  <Request>
    <HotelList>${hotelList}</HotelList>
    <Check_in_date>${checkIn}</Check_in_date>
    <Check_out_date>${checkOut}</Check_out_date>
    ${occupancyXml}
    <HotelInformation>Y</HotelInformation>
  </Request>
</SearchAvailabilityRQ>`;

  const doc = await callProxy(xmlRQ);

  const errorEl = doc.querySelector('Error');
  if (errorEl) throw new Error(errorEl.textContent || 'Veturis error');

  const hotels = [];
  const sessionID = doc.querySelector('SessionID')?.textContent || '';
  const obj       = doc.querySelector('obj')?.textContent || '';

  doc.querySelectorAll('Hotels > Hotel').forEach(h => {
    const id   = h.querySelector('HotelDetails > ID')?.textContent || '';
    const name = h.querySelector('HotelDetails > Name')?.textContent || '';
    const city = h.querySelector('HotelDetails > City')?.textContent || '';
    const lat  = h.querySelector('HotelDetails > Latitud')?.textContent || '';
    const lng  = h.querySelector('HotelDetails > Longitud')?.textContent || '';
    const photo= h.querySelector('HotelDetails > Photo > URL')?.textContent || '';
    const cat  = h.querySelector('HotelDetails > Category > Name')?.textContent || '';

    const rooms = [];
    h.querySelectorAll('Accommodations > Room').forEach(r => {
      const roomId    = r.querySelector('RoomType > ID')?.textContent || '';
      const roomName  = r.querySelector('RoomType > Name')?.textContent || '';
      const numRooms  = r.querySelector('NumberRooms')?.textContent || '1';
      const amenities = [...r.querySelectorAll('Amenities > Amenity > ID')].map(a => a.textContent);

      r.querySelectorAll('Board').forEach(b => {
        const boardId     = b.querySelector('Board_type > ID')?.textContent || '';
        const currency    = b.querySelector('Currency')?.textContent || 'EUR';
        const priceEl     = b.querySelector('Price');
        const price       = parseFloat(priceEl?.textContent || '0');
        const mandatory   = priceEl?.getAttribute('mandatory') === '1';
        const priceAgency = parseFloat(b.querySelector('PriceAgency')?.textContent || '0');
        const datos       = b.querySelector('DATOS')?.textContent || '';
        const refundable  = b.querySelector('Refundable')?.textContent || '-';
        const cancelFrom  = b.querySelector('Cancellation > From')?.textContent || '';
        const cancelPrice = parseFloat(b.querySelector('Cancellation > Price')?.textContent || '0');

        rooms.push({
          roomId, roomName, numRooms: parseInt(numRooms),
          amenities, boardId, currency,
          price, priceAgency, mandatory,
          datos, refundable,
          cancellation: cancelFrom ? { from: cancelFrom, price: cancelPrice } : null,
        });
      });
    });

    if (rooms.length > 0) {
      const cheapest = rooms.reduce((a, b) => (a.price <= b.price ? a : b));
      hotels.push({
        id, name: name.trim(), city: city.trim(), lat, lng,
        photo: photo.trim(), category: cat,
        rooms, cheapest,
        sessionObj: obj, sessionID,
      });
    }
  });

  return { hotels, sessionObj: obj, sessionID };
}

// ── 2. AdditionalInformation ──────────────────────────────────
export async function getAdditionalInformation({ obj, datos, language = 'ENG' }) {
  const xmlRQ = `<?xml version="1.0" encoding="UTF-8"?>
<AdditionalInformationRQ version="3.8" language="${language}">
  <Request>
    <obj>${obj}</obj>
    <DATOS>${datos}</DATOS>
  </Request>
</AdditionalInformationRQ>`;

  const doc = await callProxy(xmlRQ);
  const errorEl = doc.querySelector('Error');
  if (errorEl?.textContent) throw new Error(errorEl.textContent);

  const status      = doc.querySelector('AdditionalInformation > status')?.textContent;
  const pvp         = parseFloat(doc.querySelector('PVP')?.textContent || '0');
  const priceAgency = parseFloat(doc.querySelector('AdditionalInformationRS > PriceAgency')?.textContent || '0');
  const onRequest   = doc.querySelector('OnRequest')?.textContent === '1';
  const payCode     = doc.querySelector('PaymentTypes > Type')?.getAttribute('code') || '';
  const mandatory   = doc.querySelector('PVP')?.getAttribute('mandatory') === '1';

  const cancellation = [];
  doc.querySelectorAll('AdditionalInformation > Cancellation > Period').forEach(p => {
    cancellation.push({
      from:        p.querySelector('From')?.textContent || '',
      to:          p.querySelector('To')?.textContent || '',
      hour:        p.querySelector('Hour')?.textContent || '',
      amount:      parseFloat(p.querySelector('Amount')?.textContent || '0'),
      priceAgency: parseFloat(p.querySelector('PriceAgency')?.textContent || '0'),
    });
  });

  const essentialInfo = [];
  doc.querySelectorAll('EssentialInformation > Information').forEach(info => {
    essentialInfo.push({
      from:        info.querySelector('From')?.textContent || '',
      to:          info.querySelector('To')?.textContent || '',
      description: info.querySelector('Description')?.textContent || '',
    });
  });

  const hotelName    = doc.querySelector('HotelDetails > Name')?.textContent || '';
  const hotelAddress = doc.querySelector('HotelDetails > Address')?.textContent || '';
  const hotelCity    = doc.querySelector('HotelDetails > City')?.textContent || '';
  const photoUrl     = doc.querySelector('HotelDetails > Photo > URL')?.textContent || '';
  const balance      = parseFloat(doc.querySelector('AgencyBalance > Balance')?.textContent || '0');
  const credit       = parseFloat(doc.querySelector('AgencyBalance > Credit')?.textContent || '0');
  const priceChange  = doc.querySelector('PriceChange')?.textContent || null;
  const mandatoryPax = doc.querySelector('MandatoryPaxes')?.textContent || 'N';

  return {
    status,
    ok: status === '1',
    pvp, priceAgency,
    mandatory,
    onRequest, payCode,
    cancellation,
    essentialInfo,
    hotel: { name: hotelName, address: hotelAddress, city: hotelCity, photo: photoUrl },
    balance, credit,
    priceChange,
    mandatoryPax,
  };
}

// ── 3. BookingConfirmation ────────────────────────────────────
export async function confirmBooking({
  obj, datos,
  client,
  paxes = [],
  reference = '',
  comment = '',
  priceChange = null,
  mandatory = false,
  language = 'ENG',
}) {
  const paxesXml = paxes.length > 0
    ? `<paxes>${paxes.map(p => `<pax><name>${p.name}</name><surname>${p.surname}</surname></pax>`).join('')}</paxes>`
    : '';

  const xmlRQ = `<?xml version="1.0" encoding="UTF-8"?>
<BookingConfirmationRQ version="2.0" language="${language}">
  <Request>
    <obj>${obj}</obj>
    <DATOS>${datos}</DATOS>
    <client>
      <name>${client.name}</name>
      <surnames>${client.surnames}</surnames>
      <EMail>${client.email}</EMail>
      <Phone>${client.phone}</Phone>
      <PostCode>${client.postcode}</PostCode>
      ${client.documentNumber ? `<documentNumber>${client.documentNumber}</documentNumber>` : ''}
      ${client.country ? `<country>${client.country}</country>` : ''}
    </client>
    ${paxesXml}
    <payment><Type>SL</Type></payment>
    ${reference ? `<Reference>${reference}</Reference>` : ''}
    ${comment ? `<comment><text><![CDATA[${comment}]]></text></comment>` : ''}
    ${priceChange ? `<PriceChange>${priceChange}</PriceChange>` : ''}
  </Request>
</BookingConfirmationRQ>`;

  const doc = await callProxy(xmlRQ);

  const reservationStatus  = doc.querySelector('ReservationStatus')?.textContent;
  const paymentStatus      = doc.querySelector('PaymentStatus')?.textContent;
  const confirmationStatus = doc.querySelector('ConfirmationStatus')?.textContent;
  const bookingId          = doc.querySelector('BookingID')?.textContent || '';
  const securityCode       = doc.querySelector('SecurityCode')?.textContent || '';
  const locator            = doc.querySelector('Locator')?.textContent || '';
  const hasError           = doc.querySelector('ERROR')?.textContent === '1';

  const errors = [];
  doc.querySelectorAll('Errors > Error').forEach(e => {
    errors.push({ type: e.getAttribute('type') || '', message: e.textContent });
  });

  return {
    ok: confirmationStatus === '1' && bookingId && !hasError,
    reservationStatus, paymentStatus, confirmationStatus,
    bookingId, securityCode, locator, hasError, errors,
  };
}

// ── 4. BookingList ────────────────────────────────────────────
export async function getBookingList({ bookingId, language = 'ENG' }) {
  const xmlRQ = `<?xml version="1.0" encoding="UTF-8"?>
<BookingListRQ version="2.0" language="${language}">
  <Request>
    <BookingID>${bookingId}</BookingID>
  </Request>
</BookingListRQ>`;

  const doc = await callProxy(xmlRQ);
  const bookings = [];
  doc.querySelectorAll('Booking').forEach(b => {
    bookings.push({
      id:           b.getAttribute('id') || '',
      status:       b.querySelector('BookingStatus')?.textContent || '',
      locator:      b.querySelector('Locator')?.textContent || '',
      hotelName:    b.querySelector('Hotel')?.textContent || '',
      checkIn:      b.querySelector('CheckInDate')?.textContent || '',
      checkOut:     b.querySelector('CheckOutDate')?.textContent || '',
      price:        parseFloat(b.querySelector('Price')?.textContent || '0'),
      netPrice:     parseFloat(b.querySelector('NetPrice')?.textContent || '0'),
      customer:     b.querySelector('CustomerName')?.textContent || '',
      creationDate: b.querySelector('CreationDate')?.textContent || '',
    });
  });
  return bookings;
}

// ── 5. BookingCancellation ────────────────────────────────────
export async function cancelBooking({ bookingId, securityCode, confirmCancel = false }) {
  const xmlRQ = `<?xml version="1.0" encoding="UTF-8"?>
<BookingCancellationRQ>
  <Request>
    <BookingID>${bookingId}</BookingID>
    <SecurityCode>${securityCode}</SecurityCode>
    ${confirmCancel ? '<CancelConfirm>1</CancelConfirm>' : '<CancelConfirm>0</CancelConfirm>'}
  </Request>
</BookingCancellationRQ>`;

  const doc = await callProxy(xmlRQ);
  return {
    status:             doc.querySelector('BookingStatus')?.textContent || '',
    hotelName:          doc.querySelector('HotelName')?.textContent || '',
    bookingPrice:       parseFloat(doc.querySelector('BookingPrice')?.textContent || '0'),
    currentCancelPrice: parseFloat(doc.querySelector('CurrentCancellationPrice')?.textContent || '0'),
    hasError:           doc.querySelector('ERROR')?.textContent === '1',
    errorMsg:           doc.querySelector('Errors > Error')?.textContent || '',
  };
}

// ── 6. Voucher ────────────────────────────────────────────────
export async function getVoucher({ bookingId, securityCode }) {
  const xmlRQ = `<?xml version="1.0" encoding="UTF-8"?>
<VoucherRQ>
  <Request>
    <BookingID>${bookingId}</BookingID>
    <Seg>${securityCode}</Seg>
  </Request>
</VoucherRQ>`;
  return callProxy(xmlRQ);
}

// ── Board ID → name ───────────────────────────────────────────
export const BOARD_NAMES = {
  '1':  'Room Only',
  '2':  'Bed & Breakfast',
  '3':  'Half Board',
  '4':  'Full Board',
  '5':  'All Inclusive',
  '9':  'All Inclusive',
  '10': 'All Inclusive',
};
export const getBoardName = id => BOARD_NAMES[String(id)] || 'Room Only';

export const BOARD_COLORS = {
  'All Inclusive':   '#064e3b',
  'Half Board':      '#92400e',
  'Full Board':      '#4c1d95',
  'Bed & Breakfast': '#1e3a8a',
  'Room Only':       '#4b5563',
};
export const getBoardColor = name => BOARD_COLORS[name] || '#4b5563';

const EUR_TO_GBP = 0.86;
export const toGBP = (eur) => Math.ceil(eur * EUR_TO_GBP);